import type { TContact } from '../contact/models'
import type { ICaseNote } from '../note/models'
import type { ICaseDocument } from '../document/models'
import type { ITask } from '../task/models'
import type { ICompanyMSService } from '../company/models'
import type { IEmailForPost, IEmailMessage } from '../email/models'
import type {
  IEntityDataItem,
  ISelectItem,
  IEntitySelectItem,
  TDisplayString,
  TEntityId,
  INonEntityForPost,
} from '~/types/models'

/**
 * How a case came to exist (issue #1635).
 *
 * An enum rather than a `createdFromEmail` boolean, mirroring the server's
 * `CaseSource`: the ingest pipeline is already written for a second channel (a
 * RingCentral path would enqueue the same task type), which becomes a member
 * here rather than another flag.
 *
 * ⚠ The VALUES are the wire format and must match the server's literals byte
 * for byte. `source` arrives as a plain JSON string and httpGet CASTS rather
 * than validates, so a renamed value would not throw -- every comparison would
 * just quietly go false.
 *
 * ⚠ The first enum in `types/`, which is otherwise type-only. This module now
 * emits runtime JS, so reach for the members with a VALUE import
 * (`import { CaseSource }`), not `import type`.
 */
export enum CaseSource {
  MANUAL = 'manual',
  EMAIL = 'email',
}

export interface ICase extends IEntityDataItem {
  descr: string
  // When the case was opened: one datetime (WXTG/cynque-api-server#1723). It was
  // a date-only `openedDate`, with the time borrowed from `createdAt` -- which for
  // an email-created case is when the worker ran, not when the message was sent.
  openedAt: string
  type: IEntitySelectItem | null
  numLocationsAffected: number | null
  contact: TContact | null
  companyMSService: ICompanyMSService | null
  supplier: IEntitySelectItem | null
  // The company, for a case opened before anyone knows which of its services
  // it is about (cynque-api-server #1750). EXCLUSIVE with companyMSService,
  // which carries its own company: a create naming both is a 400, a PATCH
  // setting this while a service stays is a 409, and attaching a service nulls
  // it server-side. The webapp sends both on every create, and both whenever
  // either changes, one set and the other null (normalizeCompanyOrService in
  // services/cases.ts).
  company: IEntitySelectItem | null
  assignedTo: IEntitySelectItem | null
  status: ISelectItem
  // How the case came to exist (issue #1635). ⚠ SERVER-MAINTAINED, and not
  // writable from here: the column is `insert_column=False` with a `'manual'`
  // default and `ExcludeFromMergePatch`, so neither a POST nor a PATCH can set
  // it. Every case the api creates takes the default; the email-to-case
  // pathway stamps its own with a separate UPDATE. That is why there is no
  // input-field stanza for it in the case spec, and must stay that way --
  // sending it would be silently ignored rather than rejected.
  //
  // Required-and-present, not `?`: unlike the nullable fields above it always
  // has a value, so a list read never drops the key.
  source: CaseSource
  // The case this one was MERGED INTO (cynque-api-server #1658), or null. A bare
  // id, not a select item. ⚠ SERVER-MAINTAINED like `source`: set only by
  // POST /api/cases/{id}/merges, `insert_column=False` + `ExcludeFromMergePatch`,
  // and tied by a CHECK to status Merged -- so there is no input-field stanza
  // for it and it must never be sent. Optional because a payload from before
  // the merge feature carries no key.
  mergedInto?: TEntityId | null
  // ⚠ CLIENT-DERIVED, not a server property -- built by Case() and optional for
  // that reason, where everything above is required-and-nullable because the
  // server demands it on a create. It never reaches a request body: only input
  // fields do, and there is no spec stanza for it.
  //
  // The supplier the case is about, from whichever anchor it has: its own
  // `supplier` when anchored to one, otherwise the SERVICE's supplier. Same
  // shape as the derived display props on IMSService.
  supplierDescr?: TDisplayString
  // ⚠ CLIENT-DERIVED, like supplierDescr: the company the case is about --
  // its own `company` when it names one, otherwise its SERVICE's company.
  companyDescr?: TDisplayString
  // ⚠ CLIENT-DERIVED too, and optional for the same reason as supplierDescr:
  // built by Case(), never sent, no input-field stanza.
  //
  // The mdi name for the Cases list's icon column. That column carries the
  // header prop `showAsIcon`, which renders the cell VALUE as a <v-icon> -- so
  // the glyph NAME is the datum here, not a boolean.
  //
  // Set only for CaseSource.EMAIL. Left undefined for a manually-raised case,
  // which TableCard renders as an empty cell: `manual` is the overwhelming
  // majority and an icon on every row would say nothing.
  sourceIconName?: string
  // ⚠ CLIENT-DERIVED as well — built by Case(), never sent.
  //
  // `source` rendered for people: 'Email' / 'Manual' rather than the wire
  // values 'email' / 'manual'. Exists because Group By renders the grouped
  // VALUE as the group's title verbatim, with no display mapping, so grouping
  // on `source` itself would head the groups in lower case.
  sourceDescr?: TDisplayString
  // ⚠ CLIENT-DERIVED as well — built by Case(), never sent.
  //
  // Whole days since `openedAt`, or null once the case is closed.
  age?: number | null
  // ⚠ CLIENT-DERIVED as well — built by Case(), never sent.
  //
  // The open date-time on one line, the age ('N days', or '--' once closed)
  // under it: the 'Opened / Age' column.
  openedDateTimeAndAgeDescr?: TDisplayString
}

export interface ICaseType extends IEntityDataItem {
  descr: string
}

// ---------------------------------------------------------------------------
// Case overview — GET /api/cases/{id}/overview. The response body is the
// usual envelope, `{ data: ICaseOverview }`.
//
// Originally transcribed from the shapes proposed in WXTG/cynque-webapp#105,
// which derives them field by field from `CaseOverview` in the API server's
// resources/api/entities/models/case_overview.py. No longer a sketch: it is
// read by services/cases.ts getCaseOverview, pages/Cases/Overview/[id].vue,
// the caseOverviewPage store and the MSW handler, so a change here moves all
// four. Check it against that model before adding to it.
//
// ⚠ ICaseOverview is deliberately NARROWER than the payload. `CaseOverview`
// also sends `case_companies`, `case_projects`, `case_suppliers` and
// `case_tsds` — the annotation link collections — and they are left out
// here until something needs them. It also still sends a standalone
// `contact`, which is left out for good: see the note on `case` below.
// Reading is unaffected (extra keys are ignored), but do not read this type
// as proof the endpoint omits them.
//
// ⚠ Null semantics differ by ENDPOINT. The overview is built column by
// column by a view, so every key is always PRESENT — `null` or `[]` where
// empty. A list read (`GET /api/cases`) instead runs optional-and-null
// fields through `remove_unset_ts_optionals`, which DROPS the key entirely.
// That is why ICase above spells its optional fields `?: T | null` while
// the overview spells its own `T | null`.
// ---------------------------------------------------------------------------

export interface ICaseOverview {
  // Wider than what this endpoint actually sends: ICase is typed for the
  // list read, so its optional keys also admit `undefined`, which the
  // overview never emits.
  //
  // ⚠ THE CONTACT LIVES HERE, on the case, and there is no second copy.
  // This overview used to carry a standalone `contact` as well, because the
  // server built the two from different views -- `entities.case_overviews`
  // joined `entities.contacts` (the whole contact) while `entities._cases_build`
  // joined `select_entities.contacts` ({id, label}). `case.contact` is now a
  // whole TContact on both reads, so the standalone key is gone from this
  // type. The server still sends it -- kept, its model says, because the
  // webapp#105 rewrite bound it -- and nothing here reads it.
  //
  // That split was not free while it lasted: CaseHeaderCard binds
  // `overviewData.case` and renders `contact.descr`, while the page enriched
  // `overviewData.contact` -- a DIFFERENT object on the real API, and the same
  // one only under MSW, which returned a single shared reference. Enrich
  // `overviewData.case.contact` and there is nothing left to get wrong.
  case: ICase
  // ICaseNote and ICaseDocument live with the other fifteen of each, in
  // types/note/models.ts and types/document/models.ts -- they are ordinary
  // members of those families. Only ICaseEmail is declared here, because it is
  // deliberately NOT an entity.
  //
  // caseNotes and caseEmails are DISJOINT since cynque-api-server #1737: a note
  // is only a note, typed in the app or written by a worker, and a message
  // carries its own text (ICaseEmail.body). The Case page shows them on two
  // cards, Notes and Emails, rather than one merged thread.
  caseNotes: ICaseNote[]
  caseDocuments: ICaseDocument[]
  caseEmails: ICaseEmail[]
  // The Tasks whose `case` is this case, in creation order -- `[]`, never a
  // missing key (cynque-api-server #1739, PR #1791). Read-only here: writes go
  // through /api/tasks like any other task, linked by ITask.case.
  //
  // ⚠ `tasks.case_sn` has no ON DELETE action, so a case with a task reports
  // `canBeDeleted: false` -- as it does for its notes and documents.
  tasks: ITask[]
}

// A message on a case's thread: the generic IEmailMessage (types/email/
// models.ts), field for field. Its own name because the Emails card and the
// overview read it as the case's, and a case-only field would land here.
//
// `body` is NULL on an auto-reply, a bounce notification, anything over the
// spam threshold -- and the overview returns those anyway, so the Emails card
// filters them out (buildCaseEmailRows).
//
// ⚠ That text used to be a CaseNote the email pointed at (`caseNoteId`).
// Since #1737 it is `body`, and `caseNotes` holds only notes.
export interface ICaseEmail extends IEmailMessage {}

// The four annotation link entities, all the same shape. Each carries the
// LINK row, not the target it points at: `id` is the link's own id, and it
// is what you DELETE to detach. Each embeds the target's select item, so it
// still renders directly with no second call.
//
// Not referenced by ICaseOverview above — see the note in its header. They
// are reached through their own endpoints: /api/casecompanies,
// /api/caseprojects, /api/casesuppliers, /api/casetsds.
//
// ⚠ ICase.supplier and ICaseSupplier are different things, and both exist:
// the field is the one supplier the case is ABOUT, the annotation list is
// every supplier it touches.

export interface ICaseCompany extends IEntityDataItem {
  case: IEntitySelectItem
  company: IEntitySelectItem
}

export interface ICaseProject extends IEntityDataItem {
  case: IEntitySelectItem
  project: IEntitySelectItem
}

export interface ICaseSupplier extends IEntityDataItem {
  case: IEntitySelectItem
  supplier: IEntitySelectItem
}

export interface ICaseTsd extends IEntityDataItem {
  case: IEntitySelectItem
  tsd: IEntitySelectItem
}

/**
 * The body of POST /api/cases/{id}/replies -- a case's Send Email. The user
 * sets the recipients and subject (prefilled to answer the latest inbound
 * message); the threading headers that land it in the customer's existing
 * conversation stay server-side.
 *
 * cynque-api-server #1666 (PR #1672): `to` / `subject` omitted fall back to the
 * latest inbound correspondent and "Re: <descr>"; supplied, they are used as
 * sent, and `to` makes a case with no inbound email sendable. ⚠ The model is
 * `extra="forbid"`: ANY key not declared here is a 400, never ignored.
 * Validation (400): bare addresses only, none repeated across to/cc/bcc, at
 * most 50 recipients, a single-line subject -- mirrored client-side in
 * cynqueModules/emailRecipientRules.ts.
 *
 * ⚠ Since cynque-api-server #1674 (PR #1678) this is NOT the request body. The
 * endpoint is multipart-only (JSON is a 415): this object travels as the
 * `payload` part, as JSON, beside one `files` part per new attachment --
 * services/caseReply.ts builds that FormData.
 *
 * Extends INonEntityForPost: a reply is not an entity, so there is no id, hrid
 * or version to send.
 *
 * ⚠ Nothing is SENT by this request. It writes a `queued` message and returns
 * it, as an ICaseEmail (cynque-api-server #1737 -- it used to write and return a
 * CaseNote); a worker does the sending. So a 201 means "recorded", not
 * "delivered" -- watch ICaseEmail.deliveryState for that.
 */
export interface ICaseReplyForPost
  extends INonEntityForPost,
    Omit<IEmailForPost, 'body'> {
  // The message text: IEmailForPost's `body`, still named `note` on this
  // request although it is stored as the email's `body` now. Omitted from
  // IEmailForPost above rather than sent as both, which `extra="forbid"`
  // would refuse.
  note: string
  // to: omitted -> whoever last wrote in.
  // subject: omitted -> the server's "Re: <descr>" (or bare descr).
  // documentIds: documents ALREADY on this case. New files go as the multipart
  // `files` parts instead, and become case documents.
  //
  // The inbound email this answers -- an Emails row's Reply / Reply all
  // (cynque-api-server #1814). The server marks it Replied and threads onto
  // it. Omitted (the '+' Send Email), it marks the case's latest inbound.
  // ⚠ Send it ONLY when that email carries `readStatus`: a server without
  // #1814 refuses the unknown key (extra="forbid") and no reply goes out.
  inReplyToEmailId?: TEntityId
}
